import { useEffect, useState } from 'react';
import {
  Avatar,
  Badge,
  Box,
  Checkbox,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  HStack,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Spinner,
  Tag,
  Text,
  Textarea,
  useColorModeValue,
  useToast,
  VStack,
} from '@chakra-ui/react';
import {
  MdAdd,
  MdCalendarToday,
  MdCheckCircle,
  MdClose,
  MdDelete,
  MdPerson,
  MdSearch,
} from 'react-icons/md';
import Button from 'components/ui/Button';
import Steps, { Step as StepDef } from 'components/ui/Steps';
import { useCurrentDbUser } from 'hooks/useCurrentUser';
import { useEpisodesByPatient } from 'hooks/useEpisodes';
import { usePatients } from 'hooks/usePatients';
import { useCreatePlanWithSchedule } from 'hooks/usePlans';
import {
  ClinicalEpisode,
  CreatePlanWithScheduleDto,
  EstadoEpisodio,
  Patient,
  PlanWithScheduleResult,
  WeeklyDayConfig,
  WeeklyScheduleConfig,
} from 'types/models';

// ─── Steps ────────────────────────────────────────────────────────────────────

const STEPS: StepDef[] = [
  { id: 'patient',  title: 'Paciente' },
  { id: 'episode',  title: 'Episodio' },
  { id: 'plan',     title: 'Plan y horario' },
];

// ─── Day schedule helpers ─────────────────────────────────────────────────────

const DAY_KEYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const;
type DayKey = typeof DAY_KEYS[number];
const DAY_LABELS: Record<DayKey, string> = {
  monday: 'Lun', tuesday: 'Mar', wednesday: 'Mié',
  thursday: 'Jue', friday: 'Vie', saturday: 'Sáb', sunday: 'Dom',
};

function emptyDay(): WeeklyDayConfig { return { disponible: false, franjas: [] }; }

function defaultSchedule(): WeeklyScheduleConfig {
  return {
    repeticion: 'semanal',
    zonaHoraria: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Guayaquil',
    monday:    { disponible: true, franjas: [{ inicio: '08:00', fin: '18:00' }] },
    tuesday:   { disponible: true, franjas: [{ inicio: '08:00', fin: '18:00' }] },
    wednesday: { disponible: true, franjas: [{ inicio: '08:00', fin: '18:00' }] },
    thursday:  { disponible: true, franjas: [{ inicio: '08:00', fin: '18:00' }] },
    friday:    { disponible: true, franjas: [{ inicio: '08:00', fin: '18:00' }] },
    saturday:  emptyDay(),
    sunday:    emptyDay(),
  };
}

// ─── DayRow ───────────────────────────────────────────────────────────────────

function DayRow({ day, config, onChange }: { day: DayKey; config: WeeklyDayConfig; onChange: (c: WeeklyDayConfig) => void }) {
  const mutedColor  = useColorModeValue('gray.500', 'gray.400');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.200');

  return (
    <Box borderRadius='8px' border='1px solid' borderColor={borderColor} p='10px 12px' opacity={config.disponible ? 1 : 0.55}>
      <Flex align='center' gap='10px' mb={config.disponible && config.franjas.length > 0 ? '8px' : '0'}>
        <Checkbox
          isChecked={config.disponible}
          onChange={e => onChange({ ...config, disponible: e.target.checked, franjas: e.target.checked && config.franjas.length === 0 ? [{ inicio: '08:00', fin: '18:00' }] : config.franjas })}
          colorScheme='teal' size='sm'
        />
        <Text fontSize='sm' fontWeight='600' w='36px' flexShrink={0}>{DAY_LABELS[day]}</Text>
        {!config.disponible && <Text fontSize='xs' color={mutedColor}>No disponible</Text>}
        {config.disponible && (
          <IconButton aria-label='Agregar franja' icon={<Icon as={MdAdd} />} size='xs' variant='ghost' colorScheme='teal' ml='auto'
            onClick={() => onChange({ ...config, franjas: [...config.franjas, { inicio: '09:00', fin: '17:00' }] })} />
        )}
      </Flex>
      {config.disponible && config.franjas.map((f, i) => (
        <Flex key={i} align='center' gap='6px' mt='4px'>
          <Input size='xs' type='time' value={f.inicio} onChange={e => { const u = config.franjas.map((x, j) => j === i ? { ...x, inicio: e.target.value } : x); onChange({ ...config, franjas: u }); }} w='90px' borderRadius='6px' />
          <Text fontSize='xs' color={mutedColor}>–</Text>
          <Input size='xs' type='time' value={f.fin} onChange={e => { const u = config.franjas.map((x, j) => j === i ? { ...x, fin: e.target.value } : x); onChange({ ...config, franjas: u }); }} w='90px' borderRadius='6px' />
          <IconButton aria-label='Eliminar franja' icon={<Icon as={MdDelete} />} size='xs' variant='ghost' colorScheme='red'
            onClick={() => onChange({ ...config, franjas: config.franjas.filter((_, j) => j !== i) })} />
        </Flex>
      ))}
    </Box>
  );
}

// ─── Step 1: Patient search ───────────────────────────────────────────────────

function PatientStep({ onSelect }: { onSelect: (p: Patient) => void }) {
  const [query, setQuery] = useState('');
  const { data: patientsData, isLoading } = usePatients();
  const patients: Patient[] = patientsData ?? [];

  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.200');
  const inputBg     = useColorModeValue('gray.50', 'navy.700');
  const rowHover    = useColorModeValue('gray.50', 'navy.700');
  const mutedColor  = useColorModeValue('gray.400', 'gray.500');
  const textColor   = useColorModeValue('gray.800', 'white');

  const filtered = patients.filter((p: Patient) => {
    if (!query.trim()) return true;
    const q = query.toLowerCase();
    return p.nombres.toLowerCase().includes(q) || p.apellidos.toLowerCase().includes(q) || p.cedula.toLowerCase().includes(q);
  });

  return (
    <Box>
      <InputGroup mb='10px'>
        <InputLeftElement pointerEvents='none'><Icon as={MdSearch} color={mutedColor} /></InputLeftElement>
        <Input placeholder='Buscar por nombre o cédula...' value={query} onChange={e => setQuery(e.target.value)}
          bg={inputBg} border='1px solid' borderColor={borderColor} borderRadius='12px' fontSize='sm'
          _focus={{ borderColor: 'brand.500', boxShadow: 'none' }} autoFocus />
      </InputGroup>
      {isLoading && <Flex justify='center' py='24px'><Spinner size='sm' color='brand.500' /></Flex>}
      {!isLoading && filtered.length === 0 && (
        <Flex direction='column' align='center' py='24px' gap='8px'>
          <Icon as={MdPerson} w='32px' h='32px' color={mutedColor} />
          <Text fontSize='sm' color={mutedColor}>{query ? `Sin resultados para "${query}"` : 'No hay pacientes'}</Text>
        </Flex>
      )}
      {!isLoading && filtered.length > 0 && (
        <Box border='1px solid' borderColor={borderColor} borderRadius='12px' overflow='hidden' maxH='380px' overflowY='auto'>
          {filtered.map((p, i) => (
            <Flex key={p.id} align='center' gap='12px' px='14px' py='10px' cursor='pointer'
              borderTop={i > 0 ? '1px solid' : 'none'} borderTopColor={borderColor} _hover={{ bg: rowHover }} onClick={() => onSelect(p)}>
              <Avatar size='sm' name={`${p.nombres} ${p.apellidos}`} bg='brand.500' color='white' />
              <Box flex='1' minW={0}>
                <Text fontSize='sm' fontWeight='600' color={textColor} noOfLines={1}>{p.nombres} {p.apellidos}</Text>
                <Text fontSize='xs' color={mutedColor} fontFamily='mono'>{p.cedula}</Text>
              </Box>
            </Flex>
          ))}
        </Box>
      )}
    </Box>
  );
}

// ─── Step 2: Episode selection ────────────────────────────────────────────────

function EpisodeStep({ patient, onSelect, onBack }: { patient: Patient; onSelect: (e: ClinicalEpisode) => void; onBack: () => void }) {
  const { data: episodes = [], isLoading } = useEpisodesByPatient(patient.id);

  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.200');
  const rowHover    = useColorModeValue('gray.50', 'navy.700');
  const mutedColor  = useColorModeValue('gray.400', 'gray.500');
  const textColor   = useColorModeValue('gray.800', 'white');

  const active = episodes.filter((e: ClinicalEpisode) =>
    e.estado === EstadoEpisodio.ABIERTO || e.estado === EstadoEpisodio.EN_TRATAMIENTO,
  );

  return (
    <Box>
      {isLoading && <Flex justify='center' py='24px'><Spinner size='sm' color='brand.500' /></Flex>}
      {!isLoading && active.length === 0 && (
        <Box bg='orange.50' border='1px solid' borderColor='orange.200' borderRadius='10px' p='14px'>
          <Text fontSize='sm' color='orange.700' fontWeight='500'>
            Este paciente no tiene episodios clínicos activos. Crea uno desde su Historia Clínica antes de estructurar un plan.
          </Text>
        </Box>
      )}
      {!isLoading && active.length > 0 && (
        <Box border='1px solid' borderColor={borderColor} borderRadius='12px' overflow='hidden'>
          {active.map((ep: ClinicalEpisode, i: number) => (
            <Flex key={ep.id} align='flex-start' gap='12px' px='14px' py='12px' cursor='pointer'
              borderTop={i > 0 ? '1px solid' : 'none'} borderTopColor={borderColor}
              _hover={{ bg: rowHover }} onClick={() => onSelect(ep)}>
              <Box flex='1' minW={0}>
                <Flex align='center' gap='8px' mb='2px'>
                  <Text fontSize='xs' fontWeight='700' color='brand.500' fontFamily='mono'>{ep.codigoHc}</Text>
                  <Badge colorScheme={ep.estado === EstadoEpisodio.EN_TRATAMIENTO ? 'teal' : 'blue'} fontSize='9px'>
                    {ep.estado === EstadoEpisodio.EN_TRATAMIENTO ? 'En tratamiento' : 'Abierto'}
                  </Badge>
                </Flex>
                <Text fontSize='sm' fontWeight='600' color={textColor} noOfLines={1}>{ep.motivoConsulta}</Text>
                {ep.diagnosticoPrincipal && (
                  <Text fontSize='xs' color={mutedColor} noOfLines={1}>{ep.diagnosticoPrincipal}</Text>
                )}
              </Box>
            </Flex>
          ))}
        </Box>
      )}
      <Button mt='16px' variant='outline' size='sm' onClick={onBack}>← Atrás</Button>
    </Box>
  );
}

// ─── Step 3: Plan form ────────────────────────────────────────────────────────

function PlanStep({
  patient, episode, onBack, onClose, onSuccess,
}: {
  patient: Patient;
  episode: ClinicalEpisode;
  onBack: () => void;
  onClose: () => void;
  onSuccess: (result: PlanWithScheduleResult) => void;
}) {
  const toast      = useToast();
  const { data: me } = useCurrentDbUser();
  const createMut  = useCreatePlanWithSchedule();

  const [titulo,         setTitulo]         = useState('');
  const [numeroSesiones, setNumeroSesiones] = useState(8);
  const [duracionMinutos, setDuracionMinutos] = useState(60);
  const [startDate,      setStartDate]      = useState('');
  const [maxPorDia,      setMaxPorDia]      = useState(1);
  const [observaciones,  setObservaciones]  = useState('');
  const [schedule,       setSchedule]       = useState<WeeklyScheduleConfig>(defaultSchedule());

  const bgSection  = useColorModeValue('gray.50', 'whiteAlpha.50');
  const mutedColor = useColorModeValue('gray.500', 'gray.400');

  const activeDays = DAY_KEYS.filter(d => schedule[d].disponible);
  const canSubmit  = titulo.trim().length >= 3 && !!startDate && activeDays.length > 0 && !!me;

  const handleSubmit = async () => {
    if (!canSubmit || !me) return;
    const dto: CreatePlanWithScheduleDto = {
      titulo: titulo.trim(),
      numeroSesiones,
      duracionMinutos,
      professionalId: me.id,
      startDate,
      maxSesionesPorDia: maxPorDia,
      weeklySchedule: schedule,
      observaciones: observaciones.trim() || undefined,
    };
    try {
      const result = await createMut.mutateAsync({ patientId: patient.id, episodeId: episode.id, dto });
      onSuccess(result);
    } catch (err: any) {
      const msg = err?.response?.data?.message ?? 'No se pudo crear el plan';
      toast({ title: 'Error al crear plan', description: msg, status: 'error', duration: 5000, isClosable: true, position: 'top-right' });
    }
  };

  return (
    <VStack spacing='14px' align='stretch'>
      <Box bg={bgSection} borderRadius='10px' p='14px'>
        <Text fontSize='10px' fontWeight='700' color={mutedColor} textTransform='uppercase' letterSpacing='wider' mb='12px'>
          Información del Plan
        </Text>
        <VStack spacing='10px' align='stretch'>
          <FormControl isRequired>
            <FormLabel fontSize='xs' mb='4px'>Título del plan</FormLabel>
            <Input size='sm' placeholder='Rehabilitación esguince hombro…' value={titulo} onChange={e => setTitulo(e.target.value)} borderRadius='8px' maxLength={200} />
          </FormControl>
          <HStack spacing='10px'>
            <FormControl isRequired>
              <FormLabel fontSize='xs' mb='4px'>Sesiones</FormLabel>
              <NumberInput size='sm' min={1} max={60} value={numeroSesiones} onChange={v => setNumeroSesiones(Number(v))} borderRadius='8px'>
                <NumberInputField borderRadius='8px' />
                <NumberInputStepper><NumberIncrementStepper /><NumberDecrementStepper /></NumberInputStepper>
              </NumberInput>
            </FormControl>
            <FormControl isRequired>
              <FormLabel fontSize='xs' mb='4px'>Duración (min)</FormLabel>
              <NumberInput size='sm' min={15} max={180} step={5} value={duracionMinutos} onChange={v => setDuracionMinutos(Number(v))} borderRadius='8px'>
                <NumberInputField borderRadius='8px' />
                <NumberInputStepper><NumberIncrementStepper /><NumberDecrementStepper /></NumberInputStepper>
              </NumberInput>
            </FormControl>
            <FormControl isRequired>
              <FormLabel fontSize='xs' mb='4px'>Fecha inicio</FormLabel>
              <Input size='sm' type='date' value={startDate} onChange={e => setStartDate(e.target.value)} borderRadius='8px' />
            </FormControl>
          </HStack>
          <FormControl>
            <FormLabel fontSize='xs' mb='4px'>Máx. sesiones/día</FormLabel>
            <NumberInput size='sm' min={1} max={8} value={maxPorDia} onChange={v => setMaxPorDia(Number(v))} w='80px' borderRadius='8px'>
              <NumberInputField borderRadius='8px' />
              <NumberInputStepper><NumberIncrementStepper /><NumberDecrementStepper /></NumberInputStepper>
            </NumberInput>
          </FormControl>
        </VStack>
      </Box>

      <Box bg={bgSection} borderRadius='10px' p='14px'>
        <Flex align='center' justify='space-between' mb='12px'>
          <Text fontSize='10px' fontWeight='700' color={mutedColor} textTransform='uppercase' letterSpacing='wider'>
            Frecuencia Semanal
          </Text>
          {activeDays.length > 0 && (
            <Flex gap='4px' flexWrap='wrap' justify='flex-end'>
              {activeDays.map(d => (
                <Tag key={d} size='sm' colorScheme='teal' borderRadius='full' fontSize='9px'>{DAY_LABELS[d]}</Tag>
              ))}
            </Flex>
          )}
        </Flex>
        <VStack spacing='6px' align='stretch'>
          {DAY_KEYS.map(day => (
            <DayRow key={day} day={day} config={schedule[day]} onChange={cfg => setSchedule(prev => ({ ...prev, [day]: cfg }))} />
          ))}
        </VStack>
        <FormHelperText mt='8px' fontSize='xs'>
          Las sesiones se asignarán a los días disponibles en orden a partir de la fecha inicio.
        </FormHelperText>
      </Box>

      <FormControl>
        <FormLabel fontSize='xs' mb='4px'>Observaciones</FormLabel>
        <Textarea size='sm' rows={2} placeholder='Notas del plan…' value={observaciones} onChange={e => setObservaciones(e.target.value)} borderRadius='8px' resize='none' maxLength={1000} />
      </FormControl>

      <Flex gap='10px' pt='4px'>
        <Button flex='1' variant='outline' size='sm' onClick={onBack}>← Atrás</Button>
        <Button flex='2' size='sm' colorScheme='teal' leftIcon={<Icon as={MdCalendarToday} />}
          isLoading={createMut.isPending} isDisabled={!canSubmit} onClick={handleSubmit}>
          Crear Plan y Agendar
        </Button>
      </Flex>
    </VStack>
  );
}

// ─── Success state ────────────────────────────────────────────────────────────

function SuccessState({ result, onClose }: { result: PlanWithScheduleResult; onClose: () => void }) {
  const textColor  = useColorModeValue('gray.800', 'white');
  const mutedColor = useColorModeValue('gray.500', 'gray.400');

  return (
    <Flex direction='column' align='center' py='24px' gap='16px' textAlign='center'>
      <Flex w='56px' h='56px' bg='teal.500' borderRadius='full' align='center' justify='center'>
        <Icon as={MdCheckCircle} color='white' w='28px' h='28px' />
      </Flex>
      <Box>
        <Text fontSize='lg' fontWeight='800' color={textColor} mb='4px'>Plan creado exitosamente</Text>
        <Text fontSize='sm' color={mutedColor}>
          {result.sessions.length} sesiones y {result.appointments.length} citas agendadas en el calendario
        </Text>
      </Box>
      <Box w='full' bg='teal.50' borderRadius='10px' p='14px'>
        <Text fontSize='xs' fontWeight='700' color='teal.700' textTransform='uppercase' letterSpacing='wider' mb='8px'>
          Citas generadas
        </Text>
        <VStack align='stretch' spacing='4px' maxH='200px' overflowY='auto'>
          {result.appointments.map((a: any, i: number) => (
            <Flex key={a.id ?? i} justify='space-between' fontSize='xs' color='teal.800'>
              <Text fontWeight='600'>Ses. {i + 1}</Text>
              <Text>{new Date(a.scheduledAt).toLocaleDateString('es-EC', { weekday: 'short', day: 'numeric', month: 'short' })} · {new Date(a.scheduledAt).toLocaleTimeString('es-EC', { hour: '2-digit', minute: '2-digit' })}</Text>
            </Flex>
          ))}
        </VStack>
      </Box>
      <Button colorScheme='teal' size='sm' w='full' onClick={onClose}>Ver en calendario</Button>
    </Flex>
  );
}

// ─── Main Drawer ──────────────────────────────────────────────────────────────

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

export default function PlanScheduleDrawer({ isOpen, onClose }: Props) {
  const [step,    setStep]    = useState<1 | 2 | 3>(1);
  const [patient, setPatient] = useState<Patient | null>(null);
  const [episode, setEpisode] = useState<ClinicalEpisode | null>(null);
  const [result,  setResult]  = useState<PlanWithScheduleResult | null>(null);

  const bgColor     = useColorModeValue('white', 'navy.800');
  const headerColor = useColorModeValue('gray.800', 'white');
  const mutedColor  = useColorModeValue('gray.500', 'gray.400');

  useEffect(() => {
    if (!isOpen) {
      setTimeout(() => { setStep(1); setPatient(null); setEpisode(null); setResult(null); }, 300);
    }
  }, [isOpen]);

  const handleSuccess = (r: PlanWithScheduleResult) => {
    setResult(r);
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <Drawer isOpen={isOpen} placement='right' onClose={handleClose} size='md'>
      <DrawerOverlay backdropFilter='blur(2px)' bg='blackAlpha.300' />
      <DrawerContent bg={bgColor}>
        <DrawerCloseButton top='18px' />
        <DrawerHeader borderBottomWidth='1px' pb='16px'>
          <Flex align='center' gap='12px'>
            <Flex w='38px' h='38px' bg='teal.500' borderRadius='10px' align='center' justify='center' flexShrink={0}>
              <Icon as={MdCalendarToday} color='white' w='18px' h='18px' />
            </Flex>
            <Box>
              <Text fontSize='md' fontWeight='800' color={headerColor}>Nuevo Plan con Agenda</Text>
              {patient && (
                <Flex align='center' gap='6px'>
                  <Text fontSize='xs' color={mutedColor}>{patient.nombres} {patient.apellidos}</Text>
                  {episode && (
                    <>
                      <Text fontSize='xs' color={mutedColor}>·</Text>
                      <Text fontSize='xs' color='teal.500' fontWeight='600' fontFamily='mono'>{episode.codigoHc}</Text>
                    </>
                  )}
                  {!result && (
                    <Button size='xs' variant='ghost' leftIcon={<Icon as={MdClose} w='10px' h='10px' />} onClick={() => { setPatient(null); setEpisode(null); setStep(1); }} ml='4px'>
                      Cambiar
                    </Button>
                  )}
                </Flex>
              )}
            </Box>
          </Flex>
        </DrawerHeader>

        <DrawerBody py='24px' px='24px' overflowY='auto'>
          {!result && (
            <Box mb='24px'>
              <Steps steps={STEPS} activeStep={step - 1} size='sm' />
            </Box>
          )}

          {!result && step === 1 && (
            <PatientStep onSelect={p => { setPatient(p); setStep(2); }} />
          )}

          {!result && step === 2 && patient && (
            <EpisodeStep
              patient={patient}
              onSelect={ep => { setEpisode(ep); setStep(3); }}
              onBack={() => { setPatient(null); setStep(1); }}
            />
          )}

          {!result && step === 3 && patient && episode && (
            <PlanStep
              patient={patient}
              episode={episode}
              onBack={() => { setEpisode(null); setStep(2); }}
              onClose={handleClose}
              onSuccess={handleSuccess}
            />
          )}

          {result && (
            <SuccessState result={result} onClose={handleClose} />
          )}
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
}
